import { useEffect, useState } from "react";
import { Alert, Badge, Button, Form, Table } from "react-bootstrap";
import { FilterSelect, ClaimLink, money } from "../components/reports/reportShared.jsx";
import {
  getOverrunsWorklist, getOverrunsPeriodReport, checkOverrun, checkAllOnTargetOverruns,
  getCurrentUser, listEntityRows, listUsers,
} from "../services/apiClient.js";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import { formatDate, monthKey } from "../utils/date.js";

// Overruns (legacy OverrunsPage.aspx) — two reports in one:
// CURRENT = the live worklist of orders flagged for an overrun check; an admin
// ticks each off (the overrun is applied as Ignored to the newest open
// contractor invoice — orders.md/D&C rules) and it falls off the list.
// PERIOD = this/last month, financial YTD or a picked month, from the checked
// HISTORY (or live totals). 1.0 fixes: the job-type filter actually works on
// period reports (dead in 1.0); consistent half-open date windows.

const overrunBadge = (r) => {
  const given = Number(r.contract_given || 0);
  const received = Number(r.invoices_received || 0);
  const allowance = Number(r.quoted_allowance || 0);
  if (given <= 0 || allowance <= 0) return <Badge bg="secondary">TBD</Badge>;
  if (given >= received) return <Badge bg="success">On Target</Badge>;
  const over = received - given;
  return (
    <Badge bg={received <= allowance ? "warning" : "danger"} text={received <= allowance ? "dark" : undefined}>
      {money(over)} Overrun
    </Badge>
  );
};

const monthAgo = (n) => monthKey(-n); // NZ month, not UTC's

// ALWAYS ON, off the reports master switch (Robert 2026-08-01) — GPS needs the
// overrun numbers in front of them for the live-data run, not held behind the
// phase-1 verification switch.
function OverrunsReportPage() {
  const [view, setView] = useState("current"); // current | this_month | last_month | ytd | YYYY-MM
  const [source, setSource] = useState("historical"); // period reports: historical | total
  const [month, setMonth] = useState(monthAgo(0));
  const [filters, setFilters] = useState({ division_id: "", project_coordinator_id: "", job_type_id: "" });
  const [lookups, setLookups] = useState({});
  const [users, setUsers] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all(
      ["divisions", "job-types"].map((k) => listEntityRows(k).then((rows) => [k, rows]).catch(() => [k, []]))
    ).then((pairs) => setLookups(Object.fromEntries(pairs)));
    listUsers({ pco: true, activeOnly: true }).then(setUsers).catch(() => {});
    getCurrentUser().then((me) => setIsAdmin(!!me.is_admin)).catch(() => {});
  }, []);

  async function load(v = view, f = filters, s = source, m = month) {
    setError("");
    setReport(null);
    try {
      if (v === "current") {
        setReport({ kind: "current", ...(await getOverrunsWorklist(f)) });
      } else {
        const mode = v === "other" ? m : v;
        setReport({ kind: "period", ...(await getOverrunsPeriodReport({ mode, source: s, ...f })) });
      }
    } catch (err) {
      setError(err.response?.data?.error || "Unable to load the report.");
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, source, month, filters]);

  async function tickOff(orderId) {
    setBusy(true);
    try {
      await checkOverrun(orderId);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to check the overrun.");
    } finally {
      setBusy(false);
    }
  }
  async function tickAllOnTarget() {
    if (!window.confirm("Set every On Target overrun to checked?")) return;
    setBusy(true);
    try {
      const r = await checkAllOnTargetOverruns();
      setError("");
      await load();
      if (r.checked === 0) setError("No On Target overruns to check.");
    } catch (err) {
      setError(err.response?.data?.error || "Unable to check overruns.");
    } finally {
      setBusy(false);
    }
  }

  const hasOnTarget = report?.kind === "current" &&
    report.rows.some((r) => Number(r.contract_given) > 0 && Number(r.quoted_allowance) > 0 &&
      Number(r.contract_given) >= Number(r.invoices_received));

  return (
    <section className="page-panel report-doc-wide overruns-report">
      {/* PRINTS LANDSCAPE, EVERY COLUMN (KS 2026-09-16: "in portrait with no
          ability to change … make it so all columns are shown"). report-doc-wide
          is the named landscape page (report-document.css); overruns-report is
          the print sizing in print.css — smaller type, the Comments column free
          to wrap instead of demanding 260px, the contractor name no longer
          clipped, the admin action column off the page. */}
      <div className="page-heading"><h2>Overruns <RulesHelp topic="overruns" /></h2></div>

      <div className="d-flex align-items-end column-gap-3 row-gap-2 flex-wrap mb-3 no-print">
        <div>
          <Form.Label className="mb-1 small text-muted d-block">Report</Form.Label>
          {[["current", "Current Overruns"], ["this_month", "This Month"], ["last_month", "Last Month"], ["ytd", "YTD (Apr–Mar)"], ["other", "Pick a month"]].map(([v, label]) => (
            <Form.Check
              inline type="radio" key={v} id={`ov-${v}`} name="ov-view" label={label}
              checked={view === v} onChange={() => setView(v)}
            />
          ))}
        </div>
        {view === "other" ? (
          <div style={{ width: 160 }}>
            <Form.Label className="mb-1 small text-muted">Month</Form.Label>
            <Form.Control size="sm" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </div>
        ) : null}
        {view !== "current" ? (
          <div>
            <Form.Label className="mb-1 small text-muted d-block">Source</Form.Label>
            {[["historical", "Historical (as checked)"], ["total", "Live totals"]].map(([v, label]) => (
              <Form.Check
                inline type="radio" key={v} id={`ov-src-${v}`} name="ov-source" label={label}
                checked={source === v} onChange={() => setSource(v)}
              />
            ))}
          </div>
        ) : null}
        <FilterSelect label="Division" value={filters.division_id} onChange={(v) => setFilters((f) => ({ ...f, division_id: v }))} options={lookups.divisions} width={150} />
        <FilterSelect label="Project Co-Ordinator" value={filters.project_coordinator_id} onChange={(v) => setFilters((f) => ({ ...f, project_coordinator_id: v }))} options={users} labelField="full_name" width={200} />
        <FilterSelect label="Job Type" value={filters.job_type_id} onChange={(v) => setFilters((f) => ({ ...f, job_type_id: v }))} options={lookups["job-types"]} />
        {report?.kind === "current" && isAdmin && hasOnTarget ? (
          <Button size="sm" variant="outline-success" disabled={busy} onClick={tickAllOnTarget}>
            Set all On Target to checked
          </Button>
        ) : null}
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {!report ? (
        <p className="text-muted">Loading…</p>
      ) : report.kind === "current" ? (
        report.rows.length === 0 ? (
          <Alert variant="success" className="py-2">All overruns have been checked.</Alert>
        ) : (
          <>
            <Table striped bordered hover responsive size="sm" className="align-middle">
              <thead className="table-surface">
                <tr>
                  <th>Job #</th>
                  <th>Order #</th>
                  <th>Project Co-Ordinator</th>
                  <th>Division</th>
                  {/* Contractor gives width back to Comments (Karen Renall
                      2026-08-03). Comments WRAP rather than truncating with a
                      tooltip: "hover shows the full comment, which is no use on
                      paper", and this report is printed. Same call as the RDR
                      note (workflow.md W.1) — the comment is the reason the row
                      is on the list, so half a sentence is no use. The
                      contractor keeps its tooltip because a truncated NAME is
                      still recognisable, where a truncated sentence is not. */}
                  <th>Contractor</th>
                  <th>Comments</th>
                  <th className="text-end text-nowrap">Quoted Allowance</th>
                  <th className="text-end text-nowrap">Contract Given</th>
                  <th className="text-end text-nowrap">Invoices Received</th>
                  <th>Over Run</th>
                  {isAdmin ? <th className="no-print" /> : null}
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r) => (
                  <tr key={r.order_id}>
                    <td><ClaimLink claimId={r.claim_id} claimNumber={r.claim_number} tab="orders" /></td>
                    <td>{r.order_number ?? r.order_seq}</td>
                    <td className="text-nowrap">{r.project_coordinator_name || "-"}</td>
                    <td className="text-nowrap">{r.division_name || "-"}</td>
                    <td className="text-truncate" style={{ maxWidth: 130 }} data-hint={r.contractor_name || undefined}>{r.contractor_name || "-"}</td>
                    <td className="overruns-comments">{r.comments || "-"}</td>
                    <td className="text-end text-nowrap">{money(r.quoted_allowance)}</td>
                    <td className="text-end text-nowrap">{money(r.contract_given)}</td>
                    <td className="text-end text-nowrap">{money(r.invoices_received)}</td>
                    <td>{overrunBadge(r)}</td>
                    {isAdmin ? (
                      <td className="text-end no-print">
                        <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => tickOff(r.order_id)}>
                          Set to Checked
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="small text-muted">
              {report.total > report.rows.length
                ? `Total overruns: ${report.total} — first ${report.rows.length} shown.`
                : `${report.rows.length} overrun(s) to check.`}
            </div>
          </>
        )
      ) : (
        <>
          <Table striped bordered hover responsive size="sm" className="align-middle">
            <thead className="table-surface">
              <tr>
                <th>Job #</th>
                <th className="text-nowrap">Checked Date</th>
                <th>Project Co-Ordinator</th>
                <th>Contractor</th>
                <th>Comments</th>
                <th className="text-end text-nowrap">Quoted Allowance</th>
                <th className="text-end text-nowrap">Contract Given</th>
                <th className="text-end text-nowrap">Invoices Received</th>
                <th className="text-end">Over Run</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r, i) => (
                <tr key={i}>
                  <td><ClaimLink claimId={r.claim_id} claimNumber={r.claim_number} tab="orders" /></td>
                  <td className="text-nowrap">{r.checked_date ? formatDate(r.checked_date) : "-"}</td>
                  <td className="text-nowrap">{r.project_coordinator_name || "-"}</td>
                  <td className="text-truncate" style={{ maxWidth: 130 }} data-hint={r.contractor_name || undefined}>{r.contractor_name || "-"}</td>
                  <td className="overruns-comments">{r.comments || "-"}</td>
                  <td className="text-end text-nowrap">{money(r.quoted_allowance)}</td>
                  <td className="text-end text-nowrap">{money(r.contract_given)}</td>
                  <td className="text-end text-nowrap">{money(r.invoices_received)}</td>
                  <td className="text-end text-nowrap text-danger fw-semibold">{money(r.overrun)}</td>
                </tr>
              ))}
              {report.rows.length === 0 ? (
                <tr><td colSpan={9} className="text-center text-muted">No overruns in this period.</td></tr>
              ) : (
                <tr className="table-surface fw-semibold">
                  <td colSpan={8} className="text-end">Total</td>
                  <td className="text-end text-danger">{money(report.total)}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </>
      )}
    </section>
  );
}

export default OverrunsReportPage;
